import {
  CAlert,
  CButton,
  CCol,
  CCollapse,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CLink,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CTextarea,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import {
  useAllAutomationConfigTemplates,
  useAutomationConfigTemplate,
  useCreateAutomationConfigTemplate,
  useDeleteAutomationConfigTemplate,
  useUpdateAutomationConfigTemplate,
} from "src/views/products/cap-ai/cap-automation/services/AutomationListServices";

import CIcon from "@coreui/icons-react";
import { DatasetSelect } from "./DatasetModals";
import { ListSelect } from "src/reusable";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import PropTypes from "prop-types";
import { useGetDataset } from "src/views/datasets/services/DatasetServices";
import { useGetModels } from "../models/ModelService";
import { DatasetListSelect } from "../datasets/Datasets";
import { cilWarning } from "@coreui/icons";
import { DeleteItemModal } from "src/reusable/components/DeleteItemModal";
import { ProductSelect } from "./ModelModals";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useLlmScreeningEnabled } from "src/helpers/licensedRolloutGates";
import {
  isLlmModel,
  SCREENING_KIND,
} from "src/views/products/cap-ai/cap-automation/llm/llmUtils";
import ScreeningKindBadge from "src/views/products/cap-ai/cap-automation/llm/ScreeningKindBadge";

const AutomationConfigTemplateSelect = (props) => {
  const { data: actList } = useAllAutomationConfigTemplates(null, false);
  let displayItems = [];
  if (actList) {
    displayItems = actList;
  }
  return (
    <ListSelect
      handleSelectedValue={(x) => {
        //setSelectedDataset(x);
        props.handleSelectedAutomationConfigTemplate(x);
      }}
      items={displayItems}
      selectDomID="automation-config-template-list-select"
      disabled={!displayItems}
      selectedValue={props.selectedAutomationConfigTemplateId}
    />
  );
};

AutomationConfigTemplateSelect.propTypes = {
  handleSelectedAutomationConfigTemplate: PropTypes.func.isRequired,
  selectedAutomationConfigTemplateId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

export const ModelSelect = (props) => {
  const { data: modelList } = useGetModels(props.productId ?? null, true);
  // This is the classifier template's candidate-model picker, so pure-LLM
  // models are never offered: LLM screening is its own template kind
  // (screening_kind "llm", chosen above the picker behind the
  // cap-llm-screening flag) whose model comes from each config's screening
  // persona. A classifier template built on an LLM model would be a dead
  // end — the backend only accepts LLM screening settings on an llm
  // template.
  let displayItems = [];
  if (modelList) {
    // ListSelect clones and sorts by name itself
    displayItems = modelList.filter((model) => !isLlmModel(model));
  }
  return (
    <ListSelect
      handleSelectedValue={(x) => {
        props.handleSelectedModels(x);
      }}
      items={displayItems}
      selectDomID="model-list-select"
      disabled={!modelList}
      multiple={true}
    />
  );
};

ModelSelect.propTypes = {
  handleSelectedModels: PropTypes.func.isRequired,
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// "Show Advanced Settings" toggle wrapping the Raw Data JSON textarea,
// shared by the create and update modals. Open state stays with the
// parent so its resetAfterClose can collapse the section.
const AdvancedRawDataField = ({
  id,
  testId,
  open,
  onToggle,
  value,
  onChange,
}) => (
  <>
    <CFormGroup row>
      <CCol sm={12}>
        <CButton
          color="link"
          size="sm"
          style={{ padding: 0, textDecoration: "none" }}
          onClick={onToggle}
          data-testid={`${testId}-toggle`}
        >
          {open ? "Hide" : "Show"} Advanced Settings
        </CButton>
      </CCol>
    </CFormGroup>
    <CCollapse show={open}>
      <CFormGroup row>
        <CLabel className="col-sm-3 col-form-label">Raw Data</CLabel>
        <CCol sm={9}>
          <CTextarea
            id={id}
            data-testid={testId}
            type="text"
            style={{ fontSize: "85%", fontFamily: "monospace" }}
            spellCheck="false"
            rows="10"
            name="rawdata"
            placeholder="Raw Data (JSON)"
            value={value}
            onChange={onChange}
          />
        </CCol>
      </CFormGroup>
    </CCollapse>
  </>
);

AdvancedRawDataField.propTypes = {
  id: PropTypes.string.isRequired,
  testId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export const CreateAutomationConfigTemplateModal = (props) => {
  const [actName, setActName] = useState("");
  const [actDescription, setActDescription] = useState("");
  const [actParentFk, setActParentFk] = useState(null);
  const [actGroundTruthFk, setActGroundTruthFk] = useState(null);
  const [actModelFks, setActModelFks] = useState([]);
  const [actScreeningKind, setActScreeningKind] = useState(
    SCREENING_KIND.CLASSIFIER,
  );
  const [actProductFk, setActProductFk] = useState(props.defaultProductFk);
  const [actRawData, setActRawData] = useState("{}");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  // The Classifier/LLM choice exists only behind cap-llm-screening. With the
  // gate closed (or not answered yet) the form is exactly the classifier
  // form, and whatever kind state a late flag flip left behind is ignored.
  const llmScreeningEnabled = useLlmScreeningEnabled();
  const screeningKind = llmScreeningEnabled
    ? actScreeningKind
    : SCREENING_KIND.CLASSIFIER;
  const isLlmKind = screeningKind === SCREENING_KIND.LLM;
  const {
    mutate: createAutomationConfigTemplate,
    isLoading: isCreateAutomationConfigTemplateLoading,
    status: createAutomationConfigTemplateStatus,
    isError: isCreateAutomationConfigTemplateError,
    error: createAutomationConfigTemplateError,
    data: newAutomationConfigTemplate,
    reset: resetCreateAutomationConfigTemplate,
  } = useCreateAutomationConfigTemplate();

  const resetAfterClose = () => {
    setActName("");
    setActDescription("");
    setActParentFk(null);
    setActModelFks([]);
    setActScreeningKind(SCREENING_KIND.CLASSIFIER);
    setActGroundTruthFk(null);
    setActRawData("{}");
    setShowAdvanced(false);
    setErrorMessage(null);
    resetCreateAutomationConfigTemplate();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isCreateAutomationConfigTemplateError) {
    let newErrorMessage = String(createAutomationConfigTemplateError);
    if (createAutomationConfigTemplateError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        createAutomationConfigTemplateError.response.status +
        ": " +
        JSON.stringify(
          createAutomationConfigTemplateError.response.data.detail,
        );
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }

  const validationErrors = () => {
    let errors = [];
    if (actName.length === 0) {
      // validate name
      errors.push("Name cannot be empty.");
    }
    // validate act ground truth
    if (!actGroundTruthFk) {
      errors.push("Must select a ground truth dataset.");
    }
    // A classifier template needs a candidate model (the backend 422s
    // without one); an llm template names none. Checked only behind the
    // gate, so the flag-off form validates exactly as it always has.
    if (llmScreeningEnabled && !isLlmKind && actModelFks.length === 0) {
      errors.push("Must select at least one model.");
    }
    // validate rawdata
    try {
      JSON.parse(actRawData);
    } catch (error) {
      errors.push("Raw Data must be valid JSON, error when parsing:  " + error);
    }
    return errors;
  };

  const onSubmit = () => {
    let errors = validationErrors();
    if (errors.length === 0) {
      setErrorMessage(null);
      createAutomationConfigTemplate({
        name: actName,
        description: actDescription,
        parent_automation_config_template_fk: actParentFk,
        screening_kind: screeningKind,
        // The picker is hidden for an llm template; drop anything it held
        // before the switch so the request matches what the user sees.
        model_fks: isLlmKind ? [] : actModelFks,
        ground_truth_dataset_fk: actGroundTruthFk,
        product_fk: actProductFk,
        rawdata: JSON.parse(actRawData),
      });
    } else {
      setErrorMessage(errors.map((error, index) => <p key={index}>{error}</p>));
    }
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Create a new automation template</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="create-automation-template-close-button"
            data-testid="cap-tmpl-modal-create-btn-close"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={isCreateAutomationConfigTemplateLoading}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Creating automation template..."
      >
        {createAutomationConfigTemplateStatus === "success" ? (
          <CModalBody>
            <span id="create-automation-template-success-message">
              {newAutomationConfigTemplate.name} has been created!
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">Name</CLabel>
                  <CCol sm={9}>
                    <CInput
                      id="create-new-automation-template-name-input"
                      data-testid="cap-tmpl-modal-create-input-name"
                      type="text"
                      name="name"
                      placeholder="Name"
                      value={actName}
                      onChange={(e) => setActName(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">
                    Description
                  </CLabel>
                  <CCol sm={9}>
                    <CTextarea
                      id="create-new-automation-template-description-textarea"
                      data-testid="cap-tmpl-modal-create-input-desc"
                      type="text"
                      rows="2"
                      name="description"
                      placeholder="Description"
                      value={actDescription}
                      onChange={(e) => setActDescription(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">
                    Parent Automation Template (optional)
                  </CLabel>
                  <CCol sm={9}>
                    <AutomationConfigTemplateSelect
                      handleSelectedAutomationConfigTemplate={(x) => {
                        if (x !== "") {
                          setActParentFk(x);
                        } else {
                          setActParentFk(null);
                        }
                      }}
                    />
                  </CCol>
                </CFormGroup>
                {llmScreeningEnabled && (
                  <CFormGroup row>
                    <CLabel className="col-sm-3 col-form-label">
                      What screens
                    </CLabel>
                    <CCol sm={9}>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={actScreeningKind}
                        onChange={(_e, value) => {
                          // exclusive groups emit null when the active
                          // button is clicked again; keep the current kind
                          if (value) {
                            setActScreeningKind(value);
                            // The picker remounts empty on a switch back to
                            // Classifier, so drop earlier picks: the request
                            // must carry only models the user can see.
                            setActModelFks([]);
                          }
                        }}
                        aria-label="What screens for this template"
                        data-testid="cap-tmpl-modal-create-screening-kind"
                      >
                        <ToggleButton
                          value={SCREENING_KIND.CLASSIFIER}
                          data-testid="cap-tmpl-modal-create-kind-classifier"
                          sx={{ textTransform: "none" }}
                        >
                          Classifier (packaged model)
                        </ToggleButton>
                        <ToggleButton
                          value={SCREENING_KIND.LLM}
                          data-testid="cap-tmpl-modal-create-kind-llm"
                          sx={{ textTransform: "none" }}
                        >
                          LLM (screening persona)
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <small className="form-text text-muted">
                        Fixed once the template is created.
                      </small>
                    </CCol>
                  </CFormGroup>
                )}
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">Models</CLabel>
                  <CCol sm={9}>
                    {isLlmKind ? (
                      <div
                        className="col-form-label text-muted"
                        data-testid="cap-tmpl-modal-create-llm-hint"
                      >
                        None: the model and instructions come from the screening
                        persona configured on each config.
                      </div>
                    ) : (
                      <ModelSelect
                        handleSelectedModels={setActModelFks}
                        // Unscoped BY DESIGN. productId is a relevance filter,
                        // never a boundary — same capability, same visibility
                        // rules either way (Models.jsx and DatasetScoringList
                        // already pass null). Scoping it hid the model the
                        // template actually needed: a general-purpose LLM is
                        // registered to whichever product first used it, so
                        // AA-Dense sat under AtomAssist and never appeared here
                        // while CAP screening ran on it. A missing option is
                        // invisible; a surplus one announces itself the moment
                        // you try to use it, so the surplus is the better
                        // failure. never_deployed stays on and, on this path,
                        // is finally honoured — the product branch of GET
                        // /models/ drops it.
                        productId={null}
                      />
                    )}
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">
                    Ground Truth Dataset
                  </CLabel>
                  <CCol sm={9}>
                    <DatasetSelect
                      handleSelectedDataset={(x) => {
                        setActGroundTruthFk(x);
                      }}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">Product</CLabel>
                  <CCol sm={9}>
                    <ProductSelect
                      handleSelectedProducts={(x) => {
                        setActProductFk(x);
                      }}
                      selectedProducts={actProductFk}
                      allowMultiple={false}
                      disabled
                    />
                  </CCol>
                </CFormGroup>
                <AdvancedRawDataField
                  id="create-new-automation-template-rawdata-textarea"
                  testId="cap-tmpl-modal-create-input-rawdata"
                  open={showAdvanced}
                  onToggle={() => setShowAdvanced(!showAdvanced)}
                  value={actRawData}
                  onChange={(e) => setActRawData(e.target.value)}
                />
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="submit-create-new-automation-template-button"
                  data-testid="cap-tmpl-modal-create-btn-submit"
                  color="success"
                  onClick={() => onSubmit()}
                >
                  Create New Automation Template
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

CreateAutomationConfigTemplateModal.propTypes = {
  defaultProductFk: PropTypes.number,
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export const UpdateAutomationConfigTemplateModal = (props) => {
  const automationConfigTemplateId = props.id;
  const [actName, setActName] = useState("");
  const [actDescription, setActDescription] = useState("");
  const [actGroundTruthDatasetId, setActGroundTruthDatasetId] = useState("");
  const [actProductFk, setActProductFk] = useState("");
  const [actParentFk, setActParentFk] = useState(null);
  const [actRawData, setActRawData] = useState("{}");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [overwriteGroundTruth, setOverwriteGroundTruth] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState("");
  const llmScreeningEnabled = useLlmScreeningEnabled();
  const {
    mutate: updateAutomationConfigTemplate,
    isLoading: isUpdateAutomationConfigTemplateLoading,
    status: updateAutomationConfigTemplateStatus,
    isError: isUpdateAutomationConfigTemplateError,
    error: updateAutomationConfigTemplateError,
    //data: newAutomationConfigTemplate,
    reset: resetUpdateAutomationConfigTemplate,
  } = useUpdateAutomationConfigTemplate();

  const {
    data: automationConfigTemplate,
    isLoading: isAutomationConfigTemplateLoading,
  } = useAutomationConfigTemplate(automationConfigTemplateId);

  const { data: groundTruthDataset, isLoading: isGroundTruthDatasetLoading } =
    useGetDataset(automationConfigTemplate?.ground_truth_dataset_fk);

  useEffect(() => {
    setActName(automationConfigTemplate?.name);
    setActDescription(automationConfigTemplate?.description);
    setActParentFk(
      automationConfigTemplate?.parent_automation_config_template_fk,
    );
    setActRawData(JSON.stringify(automationConfigTemplate?.rawdata, null, 2));
    setActGroundTruthDatasetId(
      automationConfigTemplate?.ground_truth_dataset_fk,
    );
    setActProductFk(automationConfigTemplate?.product_fk);
  }, [automationConfigTemplate, props, groundTruthDataset]);

  const resetAfterClose = () => {
    setActName("");
    setActDescription("");
    setActParentFk(null);
    setActGroundTruthDatasetId(groundTruthDataset?.id);
    setActProductFk("");
    setActRawData("{}");
    setShowAdvanced(false);
    setOverwriteGroundTruth(false);
    setConfirmOverwrite("");
    setErrorMessage(null);
    resetUpdateAutomationConfigTemplate();
  };
  // If there was a error creating the dataset, get rid of it!
  if (isUpdateAutomationConfigTemplateError) {
    let newErrorMessage = String(updateAutomationConfigTemplateError);
    if (updateAutomationConfigTemplateError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        updateAutomationConfigTemplateError.response.status +
        ": " +
        JSON.stringify(
          updateAutomationConfigTemplateError.response.data.detail,
        );
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }

  const validationErrors = () => {
    let errors = [];
    if (actName.length === 0) {
      // validate name
      errors.push("Name cannot be empty.");
    }
    // validate act ground truth
    if (!actGroundTruthDatasetId) {
      errors.push("Must select a ground truth dataset.");
    }
    if (
      overwriteGroundTruth &&
      automationConfigTemplate?.name !== confirmOverwrite
    ) {
      errors.push(
        "Must type in name of Automation Template in order to overwrite ground truth dataset",
      );
    }
    // validate rawdata
    try {
      JSON.parse(actRawData);
    } catch (error) {
      errors.push("Raw Data must be valid JSON, error when parsing:  " + error);
    }
    return errors;
  };

  const onSubmit = () => {
    let errors = validationErrors();
    if (errors.length === 0) {
      setErrorMessage(null);
      // screening_kind is deliberately absent: it is immutable after
      // creation and the PATCH 422s on any change to it.
      updateAutomationConfigTemplate({
        id: automationConfigTemplateId,
        name: actName,
        description: actDescription,
        parent_automation_config_template_fk: actParentFk,
        ground_truth_dataset_fk: actGroundTruthDatasetId,
        product_fk: actProductFk,
        rawdata: JSON.parse(actRawData),
      });
    } else {
      setErrorMessage(errors.map((error, index) => <p key={index}>{error}</p>));
    }
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <CModal
      show={props.isVisible}
      onClose={() => onClose()}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h4>Update an automation template</h4>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="update-automation-template-close-button"
            data-testid="cap-tmpl-modal-update-btn-close"
            color="secondary"
            onClick={() => onClose()}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <LoadingOverlay
        active={
          isUpdateAutomationConfigTemplateLoading ||
          isAutomationConfigTemplateLoading ||
          isGroundTruthDatasetLoading
        }
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Loading automation template..."
      >
        {updateAutomationConfigTemplateStatus === "success" ? (
          <CModalBody>
            <span id="update-automation-template-success-message">
              Automation Template has been updated!
            </span>
          </CModalBody>
        ) : (
          <>
            <CModalBody style={{ textAlign: "left" }}>
              {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
              <CForm>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">Name</CLabel>
                  <CCol sm={9}>
                    <CInput
                      id="update-automation-template-name-input"
                      data-testid="cap-tmpl-modal-update-input-name"
                      className="col-form-input"
                      value={actName}
                      onChange={(e) => setActName(e.target.value)}
                    ></CInput>
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">
                    Description
                  </CLabel>
                  <CCol sm={9}>
                    <CTextarea
                      id="update-new-automation-template-description-textarea"
                      data-testid="cap-tmpl-modal-update-input-desc"
                      type="text"
                      rows="2"
                      name="description"
                      placeholder="Description"
                      value={actDescription}
                      onChange={(e) => setActDescription(e.target.value)}
                    />
                  </CCol>
                </CFormGroup>
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">
                    Parent Automation Template (optional)
                  </CLabel>
                  <CCol sm={9}>
                    <AutomationConfigTemplateSelect
                      selectedAutomationConfigTemplateId={actParentFk}
                      handleSelectedAutomationConfigTemplate={(x) => {
                        if (x !== "") {
                          setActParentFk(x);
                        } else {
                          setActParentFk(null);
                        }
                      }}
                    />
                  </CCol>
                </CFormGroup>
                {llmScreeningEnabled &&
                  automationConfigTemplate?.screening_kind && (
                    <CFormGroup row>
                      <CLabel className="col-sm-3 col-form-label">
                        What screens
                      </CLabel>
                      <CCol sm={9} className="col-form-label">
                        <ScreeningKindBadge
                          screeningKind={
                            automationConfigTemplate.screening_kind
                          }
                          llmScreeningEnabled
                          testId="cap-tmpl-modal-update-screening-kind"
                        />
                      </CCol>
                    </CFormGroup>
                  )}
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">Models</CLabel>
                  <CLabel
                    id="update-automation-template-models-label"
                    className="col-sm-9 col-form-label"
                  >
                    {llmScreeningEnabled &&
                      automationConfigTemplate?.screening_kind ===
                        SCREENING_KIND.LLM && (
                        <span className="text-muted">
                          None: each config&apos;s screening persona supplies
                          the model.
                        </span>
                      )}
                    {automationConfigTemplate?.automation_config_template_models?.map(
                      (model) => (
                        <li key={model.id}>
                          <CLink to={`/modeldetails/${model.model_fk}`}>
                            {model.model_name}
                          </CLink>
                        </li>
                      ),
                    )}
                  </CLabel>
                </CFormGroup>
                {overwriteGroundTruth && (
                  <CAlert
                    color="warning"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-evenly",
                      alignContent: "center",
                      maxHeight: "80px",
                    }}
                  >
                    <CIcon content={cilWarning} />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        margin: "0px",
                      }}
                    >
                      <p style={{ marginBottom: "5px", padding: "0px" }}>
                        ABOUT TO OVERWRITE GROUND TRUTH DATASET
                      </p>
                      <p style={{ margin: "0px", padding: "0px" }}>
                        AUTOMATION TEMPLATE ACCURACY STATS WILL BE AFFECTED
                      </p>
                    </div>
                    <CIcon content={cilWarning} />
                  </CAlert>
                )}
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">
                    Ground Truth Dataset
                  </CLabel>
                  <CLabel
                    id="update-automation-template-groundtruth-label"
                    className="col-sm-9 col-form-label"
                  >
                    <DatasetListSelect
                      handleSelectedDataset={(id) => {
                        if (
                          id !== "" &&
                          parseInt(id, 10) !== groundTruthDataset?.id
                        ) {
                          setOverwriteGroundTruth(true);
                        } else {
                          setOverwriteGroundTruth(false);
                        }
                        setConfirmOverwrite("");
                        setActGroundTruthDatasetId(id);
                      }}
                      selectedValue={actGroundTruthDatasetId}
                    />
                  </CLabel>
                </CFormGroup>
                {overwriteGroundTruth && (
                  <CFormGroup row>
                    <CLabel className="col-sm-6 col-form-label">
                      Type Name to Confirm Overwrite
                    </CLabel>
                    <CLabel
                      id="update-automation-template-groundtruth-label"
                      className="col-sm-6 col-form-label"
                    >
                      <CInput
                        placeholder="Automation Template Name"
                        onChange={(e) => setConfirmOverwrite(e.target.value)}
                        value={confirmOverwrite}
                        style={
                          confirmOverwrite !== automationConfigTemplate?.name
                            ? { backgroundColor: "#fadddd" }
                            : {}
                        }
                      ></CInput>
                    </CLabel>
                  </CFormGroup>
                )}
                <CFormGroup row>
                  <CLabel className="col-sm-3 col-form-label">Product</CLabel>
                  <CCol sm={9}>
                    <ProductSelect
                      handleSelectedProducts={(x) => {
                        setActProductFk(x);
                      }}
                      selectedProducts={actProductFk}
                      allowMultiple={false}
                    />
                  </CCol>
                </CFormGroup>
                <AdvancedRawDataField
                  id="update-new-automation-template-rawdata-textarea"
                  testId="cap-tmpl-modal-update-input-rawdata"
                  open={showAdvanced}
                  onToggle={() => setShowAdvanced(!showAdvanced)}
                  value={actRawData}
                  onChange={(e) => setActRawData(e.target.value)}
                />
              </CForm>
            </CModalBody>
            <CModalFooter>
              <CCol sm="12" style={{ textAlign: "right" }}>
                <CButton
                  id="submit-update-new-automation-template-button"
                  data-testid="cap-tmpl-modal-update-btn-submit"
                  color="success"
                  disabled={
                    overwriteGroundTruth &&
                    automationConfigTemplate?.name !== confirmOverwrite
                  }
                  onClick={() => onSubmit()}
                >
                  Update Automation Template
                </CButton>
              </CCol>
            </CModalFooter>
          </>
        )}
      </LoadingOverlay>
    </CModal>
  );
};

UpdateAutomationConfigTemplateModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
};

export const DeleteAutomationConfigTemplateModal = (props) => {
  const automationConfigTemplateId = props.id;
  const [confirmDelete, setConfirmDelete] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState(null);
  const {
    mutate: deleteAutomationConfigTemplate,
    isLoading: isDeleteAutomationConfigTemplateLoading,
    status: deleteAutomationConfigTemplateStatus,
    isError: isDeleteAutomationConfigTemplateError,
    error: deleteAutomationConfigTemplateError,
    reset: resetDeleteAutomationConfigTemplate,
  } = useDeleteAutomationConfigTemplate();

  const resetAfterClose = () => {
    setConfirmDelete("");
    setErrorMessage(null);
    resetDeleteAutomationConfigTemplate();
  };

  const {
    data: automationConfigTemplate,
    isLoading: isAutomationConfigTemplateLoading,
  } = useAutomationConfigTemplate(automationConfigTemplateId);

  // If there was a error deleting the dataset, show message
  if (isDeleteAutomationConfigTemplateError) {
    let newErrorMessage = String(deleteAutomationConfigTemplateError);
    if (deleteAutomationConfigTemplateError?.response?.data?.detail) {
      newErrorMessage =
        "Server Error " +
        deleteAutomationConfigTemplateError.response.status +
        ": " +
        JSON.stringify(
          deleteAutomationConfigTemplateError.response.data.detail,
        );
    }
    if (newErrorMessage !== errorMessage) {
      setErrorMessage(newErrorMessage);
    }
  }
  const onSubmit = () => {
    setErrorMessage(null);
    deleteAutomationConfigTemplate(automationConfigTemplateId);
  };

  const onClose = () => {
    resetAfterClose();
    props.onClose();
  };

  return (
    <DeleteItemModal
      isVisible={props.isVisible}
      onClose={onClose}
      itemName={automationConfigTemplate?.name}
      itemId={automationConfigTemplate?.id}
      onSubmit={onSubmit}
      confirmDeleteString={confirmDelete}
      setConfirmDeleteString={setConfirmDelete}
      errorMessage={errorMessage}
      deleteItemStatus={deleteAutomationConfigTemplateStatus}
      isDeleteItemLoading={
        isDeleteAutomationConfigTemplateLoading ||
        isAutomationConfigTemplateLoading
      }
      modalTitle="Delete Existing Automation Template"
      deleteInstructions="CONFIRM YOU WANT TO DELETE THIS AUTOMATION TEMPLATE BY TYPING AUTOMATION TEMPLATE NAME HERE, THEN CLICK DELETE"
      buttonTitle="Delete Automation Template"
    />
  );
};

DeleteAutomationConfigTemplateModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.number,
};
